import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { tasksApi } from '../services/api';
import axios from 'axios';
import PropertyHistorySearch from '../components/PropertyHistorySearch';

const API_URL = process.env.REACT_APP_BACKEND_URL;
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { 
  CalendarDays, 
  ChevronLeft,
  ChevronRight,
  Users,
  Clock,
  Home,
  ArrowLeft,
  Calendar,
  LayoutGrid,
  List,
  LogOut,
  CheckCircle2,
  AlertTriangle,
  Play
} from 'lucide-react';
import { 
  format, 
  addDays, 
  subDays, 
  startOfWeek, 
  endOfWeek, 
  startOfMonth,
  endOfMonth,
  startOfYear,
  endOfYear,
  eachDayOfInterval,
  isSameDay,
  isSameMonth,
  addMonths,
  subMonths,
  addYears,
  subYears,
  getDay
} from 'date-fns';
import { fr } from 'date-fns/locale';

const GouvernantPlanning = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [tasks, setTasks] = useState([]);
  const [housekeepers, setHousekeepers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [viewMode, setViewMode] = useState('day'); // day, week, month, year
  const [filterHousekeeper, setFilterHousekeeper] = useState('all');

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      let params = {};
      
      if (viewMode === 'day') {
        params.date = format(selectedDate, 'yyyy-MM-dd');
      } else if (viewMode === 'week') {
        const start = startOfWeek(selectedDate, { weekStartsOn: 1 });
        const end = endOfWeek(selectedDate, { weekStartsOn: 1 });
        params.date_from = format(start, 'yyyy-MM-dd');
        params.date_to = format(end, 'yyyy-MM-dd');
      } else if (viewMode === 'month') {
        const start = startOfMonth(selectedDate);
        const end = endOfMonth(selectedDate);
        params.date_from = format(start, 'yyyy-MM-dd');
        params.date_to = format(end, 'yyyy-MM-dd');
      } else if (viewMode === 'year') {
        const start = startOfYear(selectedDate);
        const end = endOfYear(selectedDate);
        params.date_from = format(start, 'yyyy-MM-dd');
        params.date_to = format(end, 'yyyy-MM-dd');
      }

      // Fetch tasks (gouvernant has access)
      const tasksRes = await tasksApi.list(params);
      setTasks(tasksRes.data);
      
      // Fetch housekeepers using gouvernant-specific endpoint
      try {
        const token = localStorage.getItem('token');
        const hkRes = await axios.get(`${API_URL}/api/gouvernant/housekeepers`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setHousekeepers(hkRes.data);
      } catch (hkError) {
        console.warn('Could not fetch housekeepers list:', hkError);
        setHousekeepers([]);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  }, [selectedDate, viewMode]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const navigateDate = (direction) => {
    if (viewMode === 'day') {
      setSelectedDate(direction === 'next' ? addDays(selectedDate, 1) : subDays(selectedDate, 1));
    } else if (viewMode === 'week') {
      setSelectedDate(direction === 'next' ? addDays(selectedDate, 7) : subDays(selectedDate, 7));
    } else if (viewMode === 'month') {
      setSelectedDate(direction === 'next' ? addMonths(selectedDate, 1) : subMonths(selectedDate, 1));
    } else if (viewMode === 'year') {
      setSelectedDate(direction === 'next' ? addYears(selectedDate, 1) : subYears(selectedDate, 1));
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      affectee: 'bg-blue-100 text-blue-700',
      en_cours: 'bg-amber-100 text-amber-700',
      termine: 'bg-emerald-100 text-emerald-700',
      probleme: 'bg-red-100 text-red-700'
    };
    const labels = {
      affectee: 'Affectée',
      en_cours: 'En cours',
      termine: 'Terminée',
      probleme: 'Problème'
    };
    return <Badge className={`${styles[status] || 'bg-gray-100'} border-0 text-xs`}>{labels[status] || status}</Badge>;
  };

  const getStatusIcon = (status) => {
    switch(status) {
      case 'termine': return <CheckCircle2 className="h-4 w-4 text-emerald-500" />;
      case 'en_cours': return <Play className="h-4 w-4 text-amber-500" />;
      case 'probleme': return <AlertTriangle className="h-4 w-4 text-red-500" />;
      default: return <Clock className="h-4 w-4 text-blue-500" />;
    }
  };

  const filteredTasks = filterHousekeeper === 'all' 
    ? tasks 
    : tasks.filter(t => t.housekeeper_ids?.includes(filterHousekeeper) || t.housekeeper_id === filterHousekeeper);

  const getDateTitle = () => {
    if (viewMode === 'day') {
      return format(selectedDate, "EEEE d MMMM yyyy", { locale: fr });
    } else if (viewMode === 'week') {
      const start = startOfWeek(selectedDate, { weekStartsOn: 1 });
      const end = endOfWeek(selectedDate, { weekStartsOn: 1 });
      return `${format(start, "d MMM", { locale: fr })} - ${format(end, "d MMM yyyy", { locale: fr })}`;
    } else if (viewMode === 'month') {
      return format(selectedDate, "MMMM yyyy", { locale: fr });
    } else {
      return format(selectedDate, "yyyy", { locale: fr });
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // Render task card (no price!)
  const TaskCard = ({ task }) => (
    <div 
      className="p-3 bg-white rounded-lg border border-slate-200 shadow-sm hover:shadow-md transition-shadow"
    >
      <div className="flex items-start gap-2">
        <div 
          className="w-2 h-full min-h-[40px] rounded-full flex-shrink-0" 
          style={{ backgroundColor: task.property_color || '#003366' }}
        />
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2">
            <span className="font-medium text-slate-800 text-sm truncate">
              {task.property_name}
            </span>
            {getStatusIcon(task.status)}
          </div>
          <p className="text-xs text-slate-500 truncate">{task.property_city}</p>
          {task.housekeeper_names?.length > 0 && (
            <div className="flex items-center gap-1 mt-1 text-xs text-slate-600">
              <Users className="h-3 w-3" />
              <span className="truncate">{task.housekeeper_names.join(' & ')}</span>
            </div>
          )}
          {task.time_spent_minutes > 0 && (
            <div className="flex items-center gap-1 mt-1 text-xs text-slate-500">
              <Clock className="h-3 w-3" />
              {task.time_spent_minutes} min
            </div>
          )}
        </div>
      </div>
    </div>
  );

  // Day View
  const DayView = () => (
    <div className="space-y-3">
      {filteredTasks.length === 0 ? (
        <p className="text-center py-12 text-slate-500">Aucune prestation ce jour</p>
      ) : (
        filteredTasks.map(task => <TaskCard key={task.id} task={task} />)
      )}
    </div>
  );

  // Week View
  const WeekView = () => {
    const start = startOfWeek(selectedDate, { weekStartsOn: 1 });
    const days = eachDayOfInterval({ start, end: endOfWeek(selectedDate, { weekStartsOn: 1 }) });

    return (
      <div className="grid grid-cols-7 gap-2">
        {days.map(day => {
          const dayTasks = filteredTasks.filter(t => t.date === format(day, 'yyyy-MM-dd'));
          const isToday = isSameDay(day, new Date());
          
          return (
            <div key={day.toISOString()} className="min-h-[200px]">
              <div className={`text-center p-2 rounded-t-lg ${isToday ? 'bg-[#003366] text-white' : 'bg-slate-100'}`}>
                <p className="text-xs font-medium">{format(day, 'EEE', { locale: fr })}</p>
                <p className="text-lg font-bold">{format(day, 'd')}</p>
              </div>
              <div className="bg-white border border-t-0 border-slate-200 rounded-b-lg p-2 space-y-2 min-h-[160px]">
                {dayTasks.map(task => (
                  <div 
                    key={task.id}
                    className="p-2 rounded text-xs"
                    style={{ backgroundColor: `${task.property_color}20` || '#00336620', borderLeft: `3px solid ${task.property_color || '#003366'}` }}
                  >
                    <p className="font-medium truncate">{task.property_name}</p>
                    <p className="text-slate-500 truncate">{task.housekeeper_names?.join(', ')}</p>
                    {getStatusBadge(task.status)}
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  // Month View
  const MonthView = () => {
    const start = startOfMonth(selectedDate);
    const end = endOfMonth(selectedDate);
    const startWeek = startOfWeek(start, { weekStartsOn: 1 });
    const endWeek = endOfWeek(end, { weekStartsOn: 1 });
    const days = eachDayOfInterval({ start: startWeek, end: endWeek });

    return (
      <div>
        <div className="grid grid-cols-7 gap-1 mb-2">
          {['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'].map(d => (
            <div key={d} className="text-center text-xs font-medium text-slate-500 py-2">{d}</div>
          ))}
        </div>
        <div className="grid grid-cols-7 gap-1">
          {days.map(day => {
            const dayTasks = filteredTasks.filter(t => t.date === format(day, 'yyyy-MM-dd'));
            const isToday = isSameDay(day, new Date());
            const isCurrentMonth = isSameMonth(day, selectedDate);
            
            return (
              <div 
                key={day.toISOString()} 
                className={`min-h-[80px] p-1 rounded-lg border ${isCurrentMonth ? 'bg-white border-slate-200' : 'bg-slate-50 border-slate-100'}`}
              >
                <p className={`text-xs font-medium mb-1 ${isToday ? 'bg-[#003366] text-white rounded-full w-6 h-6 flex items-center justify-center' : isCurrentMonth ? 'text-slate-700' : 'text-slate-400'}`}>
                  {format(day, 'd')}
                </p>
                <div className="space-y-0.5">
                  {dayTasks.slice(0, 3).map(task => (
                    <div 
                      key={task.id}
                      className="text-xs px-1 py-0.5 rounded truncate"
                      style={{ backgroundColor: `${task.property_color}30` || '#00336630' }}
                      title={`${task.property_name} - ${task.housekeeper_names?.join(', ')}`}
                    >
                      {task.property_name?.substring(0, 10)}
                    </div>
                  ))}
                  {dayTasks.length > 3 && (
                    <p className="text-xs text-slate-400 pl-1">+{dayTasks.length - 3}</p>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  // Year View
  const YearView = () => {
    const months = Array.from({ length: 12 }, (_, i) => new Date(selectedDate.getFullYear(), i, 1));
    
    return (
      <div className="grid grid-cols-3 md:grid-cols-4 gap-4">
        {months.map(month => {
          const monthTasks = filteredTasks.filter(t => {
            const taskDate = new Date(t.date);
            return taskDate.getMonth() === month.getMonth();
          });
          const completed = monthTasks.filter(t => t.status === 'termine').length;
          const problems = monthTasks.filter(t => t.status === 'probleme').length;
          
          return (
            <Card 
              key={month.toISOString()} 
              className={`border-0 shadow-sm cursor-pointer hover:shadow-md transition-shadow ${isSameMonth(month, new Date()) ? 'ring-2 ring-[#003366]' : ''}`}
              onClick={() => { setSelectedDate(month); setViewMode('month'); }}
            >
              <CardContent className="p-4">
                <h3 className="font-bold text-slate-800 capitalize mb-2">
                  {format(month, 'MMMM', { locale: fr })}
                </h3>
                <div className="space-y-1 text-sm">
                  <p className="text-slate-600">{monthTasks.length} prestations</p>
                  <div className="flex gap-2">
                    <span className="text-emerald-600">{completed} ✓</span>
                    {problems > 0 && <span className="text-red-600">{problems} !</span>}
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      {/* Header */}
      <header className="bg-[#003366] text-white px-4 py-4 shadow-lg sticky top-0 z-50">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button 
              variant="ghost" 
              size="icon"
              className="text-white hover:bg-white/20"
              onClick={() => navigate('/gouvernant')}
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="font-bold text-lg">Planning</h1>
              <p className="text-blue-200 text-sm">{user?.name}</p>
            </div>
          </div>
          <Button 
            variant="ghost" 
            className="text-white hover:bg-white/20"
            onClick={handleLogout}
          >
            <LogOut className="h-5 w-5" />
          </Button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto p-4">
        {/* Controls */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
          {/* View Mode Tabs */}
          <div className="flex gap-1 bg-white rounded-lg p-1 shadow-sm">
            {[
              { id: 'day', label: 'Jour' },
              { id: 'week', label: 'Semaine' },
              { id: 'month', label: 'Mois' },
              { id: 'year', label: 'Année' }
            ].map(mode => (
              <Button
                key={mode.id}
                variant={viewMode === mode.id ? 'default' : 'ghost'}
                size="sm"
                className={viewMode === mode.id ? 'bg-[#003366]' : ''}
                onClick={() => setViewMode(mode.id)}
              >
                {mode.label}
              </Button>
            ))}
          </div>

          {/* Date Navigation */}
          <div className="flex items-center gap-2">
            <Button 
              variant="outline" 
              size="icon"
              onClick={() => navigateDate('prev')}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <div className="min-w-[200px] text-center">
              <span className="font-semibold text-slate-800 capitalize">{getDateTitle()}</span>
            </div>
            <Button 
              variant="outline" 
              size="icon"
              onClick={() => navigateDate('next')}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => setSelectedDate(new Date())}
            >
              Aujourd'hui
            </Button>
          </div>

          {/* Filter by Housekeeper */}
          <select
            value={filterHousekeeper}
            onChange={(e) => setFilterHousekeeper(e.target.value)}
            className="px-3 py-2 rounded-lg border border-slate-200 bg-white text-sm"
          >
            <option value="all">Toutes les FDM</option>
            {housekeepers.map(hk => (
              <option key={hk.id} value={hk.id}>{hk.name}</option>
            ))}
          </select>
        </div>

        {/* Recherche logement avec historique */}
        <div className="mb-6">
          <PropertyHistorySearch 
            onNavigateToTask={(task) => {
              // Naviguer vers la date de la tâche
              setSelectedDate(new Date(task.date));
              // Pour le gouvernant, on reste sur le planning avec la bonne date
              setViewMode('day');
            }}
          />
        </div>

        {/* Stats Summary */}
        <div className="grid grid-cols-4 gap-2 mb-6">
          <div className="bg-white rounded-lg p-3 text-center shadow-sm">
            <p className="text-2xl font-bold text-slate-800">{filteredTasks.length}</p>
            <p className="text-xs text-slate-500">Total</p>
          </div>
          <div className="bg-emerald-50 rounded-lg p-3 text-center shadow-sm">
            <p className="text-2xl font-bold text-emerald-600">
              {filteredTasks.filter(t => t.status === 'termine').length}
            </p>
            <p className="text-xs text-emerald-600">Terminées</p>
          </div>
          <div className="bg-amber-50 rounded-lg p-3 text-center shadow-sm">
            <p className="text-2xl font-bold text-amber-600">
              {filteredTasks.filter(t => t.status === 'en_cours').length}
            </p>
            <p className="text-xs text-amber-600">En cours</p>
          </div>
          <div className="bg-red-50 rounded-lg p-3 text-center shadow-sm">
            <p className="text-2xl font-bold text-red-600">
              {filteredTasks.filter(t => t.status === 'probleme').length}
            </p>
            <p className="text-xs text-red-600">Problèmes</p>
          </div>
        </div>

        {/* Content */}
        <Card className="border-0 shadow-sm">
          <CardContent className="p-4">
            {loading ? (
              <div className="text-center py-12">
                <div className="animate-spin h-8 w-8 border-4 border-[#003366] border-t-transparent rounded-full mx-auto mb-4"></div>
                <p className="text-slate-500">Chargement...</p>
              </div>
            ) : (
              <>
                {viewMode === 'day' && <DayView />}
                {viewMode === 'week' && <WeekView />}
                {viewMode === 'month' && <MonthView />}
                {viewMode === 'year' && <YearView />}
              </>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default GouvernantPlanning;
